// pages/ContentDetail.jsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Typography,
  Box,
  Paper,
  Chip,
  Divider,
  IconButton,
  Button,
  CircularProgress,
  Alert,
  Stack,
} from '@mui/material';
import {
  ArrowBack,
  Edit,
  Delete,
  Article,
  CalendarToday,
  Person,
  Label,
} from '@mui/icons-material';
import { db } from '../../lib/firebase';
import { doc, getDoc, deleteDoc } from 'firebase/firestore';
import { useAuth } from '../../contexts/AuthContext';
import { deleteFileByUrl } from '../../lib/githubUpload';

const ContentDetail = () => {
  const { id } = useParams();
  const [content, setContent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { currentUser } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    fetchContent();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const fetchContent = async () => {
    try {
      const contentDoc = await getDoc(doc(db, 'contents', id));
      if (contentDoc.exists()) {
        const data = contentDoc.data();
        setContent({
          id: contentDoc.id,
          ...data,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate(),
        });
      } else {
        setError('Content not found');
      }
    } catch (err) {
      console.error('Error fetching content:', err);
      setError('Failed to load content');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this content?')) {
      try {
        // Extract and delete images from content
        const imgRegex = /<img[^>]+src="([^">]+)"/g;
        let match;
        while ((match = imgRegex.exec(content.content)) !== null) {
          await deleteFileByUrl(match[1]);
        }
        
        // Delete from Firestore
        await deleteDoc(doc(db, 'contents', id));
        navigate('/admin/content');
      } catch (err) {
        console.error('Error deleting content:', err);
        alert('Failed to delete content');
      }
    }
  };

  const handleImportToBlog = () => {
    navigate('/admin/blogs/create', { state: { importedContent: content } });
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error}</Alert>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/content')} sx={{ mt: 2 }}>
          Back to Content
        </Button>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/admin/content')}>
          Back to Content
        </Button>
        <Box>
          <Button
            variant="outlined"
            startIcon={<Article />}
            onClick={handleImportToBlog}
            sx={{ mr: 1 }}
          >
            Import to Blog
          </Button>
          {currentUser && currentUser.uid === content.userId && (
            <>
              <IconButton onClick={() => navigate(`/admin/content/edit/${content.id}`)}>
                <Edit />
              </IconButton>
              <IconButton onClick={handleDelete} color="error">
                <Delete />
              </IconButton>
            </>
          )}
        </Box>
      </Box>

      <Paper elevation={3} sx={{ p: 4 }}>
        <Box display="flex" justifyContent="space-between" alignItems="start" mb={2}>
          <Chip 
            label={content.category} 
            color={content.status === 'published' ? 'success' : 'default'} 
          />
          {content.status === 'draft' && (
            <Chip label="Draft" size="small" color="warning" />
          )}
        </Box>

        <Typography variant="h3" component="h1" gutterBottom fontWeight="bold">
          {content.title}
        </Typography>

        <Box display="flex" gap={3} mb={3} flexWrap="wrap">
          <Box display="flex" alignItems="center" gap={1}>
            <Person fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              {content.author}
            </Typography>
          </Box>
          <Box display="flex" alignItems="center" gap={1}>
            <CalendarToday fontSize="small" color="action" />
            <Typography variant="body2" color="text.secondary">
              Created: {content.createdAt?.toLocaleDateString()}
            </Typography>
          </Box>
          {content.updatedAt && content.updatedAt !== content.createdAt && (
            <Box display="flex" alignItems="center" gap={1}>
              <CalendarToday fontSize="small" color="action" />
              <Typography variant="body2" color="text.secondary">
                Updated: {content.updatedAt?.toLocaleDateString()}
              </Typography>
            </Box>
          )}
        </Box>

        {content.tags && content.tags.length > 0 && (
          <Box display="flex" alignItems="center" gap={1} mb={3}>
            <Label fontSize="small" color="action" />
            <Stack direction="row" spacing={1}>
              {content.tags.map((tag) => (
                <Chip key={tag} label={tag} size="small" variant="outlined" />
              ))}
            </Stack>
          </Box>
        )}

        <Divider sx={{ my: 3 }} />

        <Box
          sx={{
            '& h1': { fontSize: '2em', fontWeight: 'bold', margin: '0.67em 0' },
            '& h2': { fontSize: '1.5em', fontWeight: 'bold', margin: '0.83em 0' },
            '& h3': { fontSize: '1.17em', fontWeight: 'bold', margin: '1em 0' },
            '& p': { margin: '1em 0', lineHeight: 1.6 },
            '& ul, & ol': { margin: '1em 0', paddingLeft: '2em' },
            '& li': { margin: '0.5em 0' },
            '& blockquote': {
              margin: '1em 0',
              paddingLeft: '1em',
              borderLeft: '4px solid #1976d2',
              color: '#666',
              fontStyle: 'italic',
            },
            '& pre': {
              backgroundColor: '#f4f4f4',
              padding: '1em',
              borderRadius: '4px',
              overflow: 'auto',
              fontFamily: 'monospace',
            },
            '& code': {
              backgroundColor: '#f4f4f4',
              padding: '0.2em 0.4em',
              borderRadius: '3px',
              fontFamily: 'monospace',
            },
            '& img': {
              maxWidth: '100%',
              height: 'auto',
              margin: '1em 0',
            },
            '& a': {
              color: '#1976d2',
              textDecoration: 'underline',
            },
          }}
          dangerouslySetInnerHTML={{ __html: content.content }}
        />
      </Paper>
    </Container>
  );
};

export default ContentDetail;